import { Box, MenuItem, Typography } from '@mui/material'
import Viewer, { ViewerRef } from '../components/viewer/Viewer'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import { convertTo3DAsync } from '../services/filesService'
import { isDevelop } from '../utils/Common'
import { ConstructionPage, ConstructionTab } from '../reducers/construction'
import ViewerOverlay from '../components/viewer/ViewerOverlay'
import Permit from '../components/Permit/Permit'
import {
  resetConstructionState,
  setIs3DFetchBuildingInfo,
  setIs3DViewActive,
} from '../actions/construction'
import { useOnResizeObserver } from '../hooks/useOnResizeObserver'
import { fetchCommentsAsync } from '../services/commentsService'
import AddComment from '../components/Comments/AddCommentComponent'
import ManageComment from '../components/Comments/ManageCommentComponent'
import { commentColorMap, CommentDiscipline } from '../models/Comment'
import CustomDropdown from '../components/CustomDropdown'
import { resetCommentsState, updateAddCommentInfo } from '../actions/comments'
import { Can } from '../utils/AbilityUtil'
import {
  architectureRoles,
  AttributeActionNames,
  AttributeResourceNames,
  engineeringRoles,
} from '../models/AccessControl'
import AssignProjectComponent from '../components/AssignProject/AssignProjectComponent'
import {
  showAssignProjectDialog,
  showOneTimeServicePaymentDialog,
  showUpdatePaymentMethodDialog,
} from '../actions/organization'
import { resetCodeValidationState } from '../actions/validation'
import { resetStructuralState } from '../actions/structural'
import SelectedCostEstimation from '../components/TakeOff/SelectedCostEstimation'
import QuickSelect from '../components/TakeOff/QuickSelect/QuickSelect'
import TakeOffBreakdown from '../components/TakeOff/TakeOffBreakdown/TakeOffBreakdown'
import { createSelector } from '@reduxjs/toolkit'
import SavePaymentMethodModal from '../components/Payment/SavePaymentMethodModal'
import OneTimeServicePaymentModal from '../components/Payment/OneTimeServicePaymentModal'
import { fetchBuildingInfo } from '../services/buildingService'
import { BuildingDocumentType } from '../models/Construction'
import Viewer2D from '../components/viewer/Viewer2D'
import { fetchBimObjectsAsync } from '../services/bimService'
import { resetBimState } from '../actions/bim'
import BimElementViewer from '../components/BIM/BimElementViewer'
import { IfcLoader } from '../visualization/ifcLoader'

const selectComments = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.comments.commentClusters,
    (state: RootState) => state.comments.commentsDisciplineFilter,
  ],
  (selectedConstructionTab, commentClusters, commentsDisciplineFilter) => {
    const tab = commentClusters.find(
      (cluster) => cluster.tab === selectedConstructionTab,
    )

    return tab
      ? commentsDisciplineFilter
        ? tab.comments.filter(
            (comment) => comment.discipline === commentsDisciplineFilter,
          )
        : tab.comments
      : []
  },
)

const selectSelectedItemDrawind = createSelector(
  [
    (state: RootState) => state.construction.convertData.building.storeys,
    (state: RootState) => state.construction.selectedStoreyId,
    (state: RootState) => state.construction.documents,
  ],
  (storeys, selectedStoreyId, documents) => {
    const storeyIndex = storeys.findIndex(
      (storey) => storey.id === selectedStoreyId,
    )
    const floorplan2D = documents.find(
      (document) => document.type === BuildingDocumentType.Floorplans2D,
    )

    const page = floorplan2D?.file.pages[storeyIndex]
    return page?.image ?? ''
  },
)

function Building() {
  const dispatch = useDispatch<any>()
  const [ref, setRef] = useState<HTMLElement | null>(null)
  const viewerRef = useRef<ViewerRef>(null)
  const is3DViewActive =
    useSelector((state: RootState) => state.construction.is3DViewActive) ?? true
  const is2DViewActive =
    useSelector((state: RootState) => state.construction.is2DViewActive) ??
    false
  const isLoadingEngineerOrganization =
    useSelector(
      (state: RootState) => state.review.isLoadingEngineerOrganization,
    ) ?? false
  const [imageHeight, setImageHeight] = useState(0)
  const [viewerDimensions, setViewerDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight - 52,
  })

  const [view3dImage, setView3dImage] = useState<string>()

  const user = useSelector((state: RootState) => state.auth.user)
  const storeys = useSelector(
    (state: RootState) => state.construction.convertData.building.storeys,
  )
  const roofs = useSelector(
    (state: RootState) => state.construction.convertData.building.roofs,
  )
  const isFetch3DBuildingInfo = useSelector(
    (state: RootState) => state.construction.isFetch3DBuildingInfo,
  )
  const selectedItemDrawing = useSelector(selectSelectedItemDrawind)

  const organization = useSelector((state: RootState) => state.organization)
  const selectedFile = useSelector(
    (state: RootState) => state.organization.selectedFile,
  )
  const showUploadModal = useSelector(
    (state: RootState) => state.construction.showUploadModal,
  )

  const handleViewsChange = async () => {
    dispatch(setIs3DViewActive(!is3DViewActive))
    if (viewerRef.current) {
      setView3dImage(await viewerRef.current.get3DViewImage())
    }
  }

  const selectedConstructionTab = useSelector(
    (state: RootState) => state.construction.selectedConstructionTab,
  )
  const selectedEditorPage = useSelector(
    (state: RootState) => state.construction.selectedConstructionPage,
  )
  const isTakeOffView = selectedConstructionTab === ConstructionTab.TakeOff
  const isPermitView = selectedConstructionTab === ConstructionTab.Permit

  const comments = useSelector(selectComments)
  const isCommentsMode = useSelector(
    (state: RootState) => state.comments.isCommentsMode,
  )
  const isAddCommentMode = useSelector(
    (state: RootState) => state.comments.isAddCommentMode,
  )
  const addCommentPosition = useSelector(
    (state: RootState) => state.comments.addCommentInfo.position,
  )

  const architectFilePath = useSelector(
    (state: RootState) => state.organization.selectedFile?.path,
  )
  const engineerFilePath = useSelector(
    (state: RootState) => state.review.selectedProject?.file.path,
  )
  const selectedFilePath = user
    ? architectureRoles.includes(user.role.name)
      ? architectFilePath
      : engineeringRoles.includes(user.role.name)
        ? engineerFilePath
        : null
    : null

  const selectedTabResourse = () => {
    if (selectedConstructionTab === ConstructionTab.Editor) {
      if (selectedEditorPage === ConstructionPage.Architecture) {
        return AttributeResourceNames.Architecture
      } else if (selectedEditorPage === ConstructionPage.Engineering) {
        return AttributeResourceNames.StructuralEngineering
      }
    } else if (selectedConstructionTab === ConstructionTab.TakeOff) {
      return AttributeResourceNames.TakeOff
    } else if (selectedConstructionTab === ConstructionTab.Validation) {
      return AttributeResourceNames.Validation
    } else if (selectedConstructionTab === ConstructionTab.Permit) {
      return AttributeResourceNames.Permit
    }

    return AttributeResourceNames.Architecture
  }

  const onRefChange = useCallback((node: HTMLElement) => {
    setRef(node) // or change other state to re-render
  }, [])

  useEffect(() => {
    const updateImageSize = () => {
      if (ref) {
        const { offsetHeight } = ref
        setImageHeight(offsetHeight - 36)
      }
    }

    updateImageSize()

    window.addEventListener('resize', updateImageSize)

    return () => {
      window.removeEventListener('resize', updateImageSize)
    }
  }, [ref])

  useOnResizeObserver(ref!, ({ width, height }) => {
    setViewerDimensions({
      width,
      height,
    })
  })

  useEffect(() => {
    if (isDevelop()) {
      dispatch(convertTo3DAsync())
    } else if (isFetch3DBuildingInfo && selectedFilePath) {
      // TODO: refactor to make abort controller in global place
      dispatch(fetchBuildingInfo({ fileKey: selectedFilePath ?? '' }))

      return () => {
        //        controller.abort()
        if (!showUploadModal) {
          dispatch(resetConstructionState())
          dispatch(resetStructuralState())
          dispatch(resetCodeValidationState())
          dispatch(setIs3DFetchBuildingInfo(false))
        }
      }
    }
  }, [isFetch3DBuildingInfo, selectedFilePath, dispatch])

  useEffect(() => {
    const abortController = new AbortController()
    if (user && architectureRoles.includes(user.role.name) && is3DViewActive) {
      dispatch(
        fetchCommentsAsync({
          tab: ConstructionTab.Editor,
          file_id: selectedFile?.id,
        }),
      )
      dispatch(fetchBimObjectsAsync({ signal: abortController.signal }))
    }

    return () => {
      abortController.abort()
      dispatch(resetCommentsState())
      dispatch(resetBimState())
      IfcLoader.getInstance().dispose()
    }
  }, [user])

  if (!storeys.length && !roofs.length) return <Box />

  const AddCommentHeader = () => {
    const commentDisciplinesList: CommentDiscipline[] = Object.keys(
      CommentDiscipline,
    )
      .filter((key) => isNaN(Number(key)))
      .map((key) => CommentDiscipline[key as keyof typeof CommentDiscipline])

    const handleDisciplineClick = (discipline: CommentDiscipline) => {
      dispatch(updateAddCommentInfo({ commentDiscipline: discipline }))
    }

    const addCommentDiscipline = useSelector(
      (state: RootState) => state.comments.addCommentInfo.commentDiscipline,
    )

    return (
      <CustomDropdown
        title={
          addCommentDiscipline ? addCommentDiscipline : 'Choose discipline'
        }
        color='#fff'
        buttonStyleProps={{
          justifyContent: 'center',
          marginBottom: '7px',
          padding: '5px',
          width: '118px',
          background: addCommentDiscipline
            ? commentColorMap[addCommentDiscipline]
            : '#04002E',
          borderRadius: '72px',
        }}
        titleStyleProps={{
          fontSize: '10px',
        }}
      >
        {commentDisciplinesList
          .filter((item) => item !== addCommentDiscipline)
          .map((item, index) => (
            <MenuItem
              key={index}
              sx={{
                background: commentColorMap[item],
                color: '#fff',
                '&:hover': {
                  color: '#04002E',
                },
              }}
              onClick={() => handleDisciplineClick(item)}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                }}
              >
                {item}
              </Typography>
            </MenuItem>
          ))}
      </CustomDropdown>
    )
  }

  return (
    <Box
      sx={{
        gap: '12px',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 52px)',
        boxSizing: 'border-box',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      {/* Permit */}
      {isPermitView ? (
        <Permit></Permit>
      ) : (
        <>
          {/* Construction Take Off */}
          {isTakeOffView && (
            <Can
              I={AttributeActionNames.Read}
              a={AttributeResourceNames.TakeOff}
            >
              <TakeOffBreakdown />
            </Can>
          )}

          {/* 3D Viewer and Area Quick Select */}
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              gap: '14px',
              overflow: 'hidden',
            }}
          >
            {/* Area Quick Select */}
            {isTakeOffView && (
              <Can
                I={AttributeActionNames.Read}
                a={AttributeResourceNames.TakeOff}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '0px 0px 12px 12px',
                  }}
                >
                  <SelectedCostEstimation />

                  <QuickSelect />
                </Box>
              </Can>
            )}
            {/* 3D Viewer */}
            <Can I={AttributeActionNames.Read} a={selectedTabResourse()}>
              <Box
                ref={onRefChange}
                sx={{
                  flexGrow: 1,
                  backgroundColor: '#FFF',
                  borderRadius: `${isTakeOffView ? 4 : 0}px`,
                  border: '1px solid #D8D8D8',
                  boxShadow: '0px 3px 5px 0px rgba(47, 47, 47, 0.25)',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                  margin: isTakeOffView ? '0px 12px 12px 0px' : 0,
                }}
              >
                <>
                  {is2DViewActive ? (
                    <Viewer2D />
                  ) : is3DViewActive ? (
                    <Viewer ref={viewerRef} />
                  ) : (
                    <Box
                      sx={{
                        minWidth: '500px',
                        flexGrow: 1,
                        display: 'flex',
                        borderRadius: 2,
                        boxShadow: 3,
                        position: 'relative',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxHeight: viewerDimensions.height,
                        maxWidth: viewerDimensions.width,
                      }}
                    >
                      <img
                        src={selectedItemDrawing}
                        alt='view3dImage'
                        style={{
                          height: `${imageHeight}px`,
                        }}
                      />
                    </Box>
                  )}

                  <ViewerOverlay
                    viewerSize={viewerDimensions}
                    imageHeight={imageHeight}
                    expandableImageSrc={
                      is3DViewActive ? selectedItemDrawing! : view3dImage!
                    }
                    onSwitchViews={handleViewsChange}
                  />
                  <BimElementViewer isHidden={true} />
                </>
              </Box>
            </Can>
          </Box>
        </>
      )}

      {isCommentsMode && isAddCommentMode && (
        <AddComment
          tab={selectedConstructionTab}
          position={{
            x: addCommentPosition.x,
            y: addCommentPosition.y - 33,
          }}
          header={{ component: <AddCommentHeader />, height: 14 }}
        />
      )}
      {isCommentsMode &&
        comments
          .filter(
            (comment) =>
              !comment.isResolved && comment.positionX && comment.positionY,
          )
          .map((comment, index) => (
            <ManageComment
              key={index}
              tab={selectedConstructionTab}
              comment={comment}
            />
          ))}

      <AssignProjectComponent
        isOpen={organization.showAssignProjectModal}
        handleClose={() => dispatch(showAssignProjectDialog(false))}
        isLoading={isLoadingEngineerOrganization}
      />
      <SavePaymentMethodModal
        isOpen={organization.showUpdatePaymentMethodModal}
        handleClose={() => dispatch(showUpdatePaymentMethodDialog(false))}
      />
      <OneTimeServicePaymentModal
        isOpen={organization.showOneTimeServicePaymentModal}
        handleClose={() => dispatch(showOneTimeServicePaymentDialog(false))}
      />
    </Box>
  )
}

export default Building
